import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { InviteTarget } from '../types';
import { inviteAPI } from '@/api/inviteAPI';
import type { ContactEmployeeNode, ContactListItem } from '@/types/employee';
import { getChoseong } from '@/utils/choseong';
import { toAbsoluteProfileUrl } from '@/utils/profileUtils';
import { formatPhoneNumber } from '@/utils/formatUtils';

// Local alias
type EmployeeNode = ContactEmployeeNode;

interface ContactTabProps {
  query: { text: string; version: number };
  selectedUsers: InviteTarget[];
  onToggleUser: (user: InviteTarget) => void;
  cno: string | null;
  companyNo: string | null;
}

const ContactTab: React.FC<ContactTabProps> = ({ query, selectedUsers, onToggleUser, cno, companyNo }) => {
  const { data: employees = [] as EmployeeNode[], isLoading } = useQuery({
    queryKey: ['contacts', query.text],
    queryFn: async (): Promise<EmployeeNode[]> => {
      const res = await inviteAPI.getContactList(query.text);
      if (res && res.resultCode === 200 && res.resultData && res.resultData.contactsList) {
        return res.resultData.contactsList.map((c: ContactListItem) => {
          const phone = c.phoneinfolist && c.phoneinfolist.length > 0
            ? (c.phoneinfolist[0].phone_number || '')
            : (c.phone_number || '');
          const email = c.emailinfolist && c.emailinfolist.length > 0
            ? (c.emailinfolist[0].email_address || '')
            : (c.email_address || '');
            
          const company = c.companyinfolist && c.companyinfolist.length > 0
            ? (c.companyinfolist[0].company_name || '')
            : '';
            
          const rank = c.companyinfolist && c.companyinfolist.length > 0
            ? (c.companyinfolist[0].rank_name || '')
            : '';

          return {
            user_no: c.user_no_o_company_no || 0,
            employee_no: c.address_service_no || 0,
            user_name: c.address_name || '',
            company_name: company,
            rank_name: rank,
            company_no: Number(companyNo) || 0,
            profile_url: c.profile_image || undefined,
            user_email: email,
            user_contact: phone
          };
        });
      }
      return [];
    },
    placeholderData: (prev) => prev,
    staleTime: 1000 * 60 * 5,
  });

  const selectedIds = useMemo(() => new Set(selectedUsers.map(u => u.id)), [selectedUsers]);

  const groupedEmployees = useMemo(() => {
    const groups: Record<string, EmployeeNode[]> = {};
    employees.forEach(emp => {
      const initChar = getChoseong(emp.user_name || '?');
      if (!groups[initChar]) groups[initChar] = [];
      groups[initChar].push(emp);
    });
    return groups;
  }, [employees]);

  if (isLoading && employees.length === 0) {
    return <div className="rtcOrganization__top chatbox--empty scrollCustom">
        <div className="empty_area">
          <span className="ico"><img src={"https://static.wehago.com/imgs/common/ico_nodata140.png"} width="42" alt=""/></span>
          <p className="empty_txt">불러오는 중입니다...</p>
        </div>
      </div>;
  }

  if (employees.length === 0) {
    return (
      <div className={`rtcOrganization__top chatbox--empty scrollCustom ${query.text ? 'rtcNew_joinMemberListWrap' : ''}`}>
        <div className="empty_area">
          <span className="ico">
            <img 
              src={query.text ? "https://static.wehago.com/imgs/common/ico_nodata138.png" : "https://static.wehago.com/imgs/common/ico_nodata140.png"} 
              width="42" 
              alt=""
            />
          </span>
          <p className="empty_txt">
            {query.text ? (
              <>찾으시는 참석자가 없어요.<br />이름을 다시 확인해주세요.</>
            ) : (
              <>이메일을 입력하여 참석자에게<br />초대 메일을 보내보세요!</>
            )}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="rtcOrganization__top scrollCustom">
      {Object.keys(groupedEmployees).sort().map(sectionChar => (
        <React.Fragment key={sectionChar}>
          <div className="rtcOrganization__adSection">{sectionChar}</div>
          {groupedEmployees[sectionChar].map(emp => {
            const contact = emp.user_contact || emp.employee_contact || '';
            const email = emp.user_email || emp.user_default_email || '';
            const phoneDigits = contact.replace(/\D/g, '');
            // 연락처 API에 있는 번호이므로 엄격한 정규식 대신 번호 존재 여부만 확인합니다.
            const targetType: 'CONTACT' | 'EMAIL' | null = phoneDigits.length > 0
              ? 'CONTACT'
              : (email ? 'EMAIL' : null);
            if (!targetType) return null;
            const targetId = targetType === 'CONTACT'
              ? `CONTACT_${phoneDigits}`
              : `EMAIL_${email}`;
            const isSelected = selectedIds.has(targetId);
            const bgImage = toAbsoluteProfileUrl(emp.profile_url) || '';
            const formattedContact = formatPhoneNumber(contact) || contact;

            return (
              <div key={targetId} className="rtcOrganization__adItem">
                <div className="rtcOrganization__adItemLeft">
                  <div className="common_coProfileBox">
                    <div 
                      className="common_coProfileBox__imgBox"
                      style={bgImage ? { backgroundImage: `url(${bgImage})` } : {}}
                    />
                  </div>
                  <div className="infoBox">
                    <div className="infoBox__name">
                      <em className="ellipsis">{emp.user_name}</em>
                      {emp.company_name && (
                        <span className="ellipsis" style={{ marginLeft: '8px' }}>
                          {emp.company_name}
                        </span>
                      )}
                    </div>
                    <div className="infoBox__number">
                      {contact && <span className="infoBox__flexFix ellipsis">{formattedContact}</span>}
                      {contact && email && <span className="bar"></span>}
                      {email && <span className="ellipsis">{email}</span>}
                    </div>
                  </div>
                </div>
                <div className="rtcOrganization__adItemRight">
                  <div className="common_radio_input type_agree5 rtcNewType">
                    <label>
                      <input 
                        className="radio_input" 
                        type="checkbox" 
                        checked={isSelected}
                        onChange={() => onToggleUser({
                          id: targetId,
                          type: targetType,
                          name: emp.user_name || '이름없음',
                          profileUrl: bgImage || undefined,
                          department: emp.company_name || emp.rank_name,
                          phone: targetType === 'CONTACT' ? phoneDigits : undefined,
                          email: targetType === 'EMAIL' ? email : (email || undefined)
                        })}
                      />
                      <span className="icon"></span>
                    </label>
                  </div>
                </div>
              </div>
            );
          })}
        </React.Fragment>
      ))}
    </div>
  );
};

export default ContactTab;
